import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient, PLAYGROUND } from '../services/apiClient';
import { Auth, cookieUtils } from '../utils';
import { getUserFromToken, setToken } from '../services/user';
import GoogleLogin from './GoogleLogin';

type CostEntry = { inputCost?: number; outputCost?: number; totalCost?: number };
type ChildDoc = {
    scanGradeId: string;
    scanGradeHomeworkId?: string;
    questionId?: string;
    questionText?: string;
    costs?: Record<string, CostEntry> & { totalCost?: number };
    tokens?: Record<string, any>;
};
type ParentDoc = ChildDoc & {
    isParent?: boolean;
    numQuestions?: number;
    children?: string[];
    scanGrade?: { segmentationPerQuestion?: { costs?: CostEntry; tokens?: Record<string, number> } };
};
type Response = { parent: ParentDoc | null; children: ChildDoc[] };

const formatCost = (n?: number) => `$${(n ?? 0).toFixed(6)}`;

const sumGradingCost = (costs?: ChildDoc['costs']) => {
    if (!costs) return 0;
    let total = 0;
    for (const [key, val] of Object.entries(costs)) {
        if (key.startsWith('scanGradeGrading_') && val && typeof val === 'object') {
            total += (val as CostEntry).totalCost || 0;
        }
    }
    return total;
};

const ScanGradeHomeworkPage: React.FC = () => {
    const { scanGradeHomeworkId } = useParams<{ scanGradeHomeworkId: string }>();
    const [data, setData] = useState<Response | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<any | null>(null);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    const googleLoginUrl = `${process.env.REACT_APP_PLATFORM_URL}/auth/google?redirect=${window.location.href}`;
    const token = cookieUtils.get(Auth.TOKEN);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');
        if (urlToken) {
            setToken(urlToken);
        }
    }, []);

    useEffect(() => {
        getUserFromToken()
            .then((userData) => setUser(userData && userData.id ? userData : null))
            .catch(() => setUser(null))
            .finally(() => setIsCheckingAuth(false));
    }, [token]);

    useEffect(() => {
        if (!scanGradeHomeworkId) {
            setError('No homework id provided');
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const resp = await apiClient.get<Response>(
                    `/api/scan-grade/homework/${scanGradeHomeworkId}`,
                    PLAYGROUND
                );
                if (!cancelled) setData(resp);
            } catch (err: any) {
                if (!cancelled) setError(err?.message || 'Failed to load homework');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [scanGradeHomeworkId]);

    if (isCheckingAuth) {
        return <div style={{ padding: 50, textAlign: 'center' }}><h2>Checking authentication...</h2></div>;
    }
    if (!user) {
        return (
            <div style={{ width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <GoogleLogin
                    loginUrl={googleLoginUrl}
                    onLoginClick={() => {}}
                    isOpen={true}
                    onClose={() => {}}
                    disabled={false}
                    title="Sign in to view homework"
                    description="Please sign in to access scan-and-grade homework details."
                />
            </div>
        );
    }
    if (loading) return <div style={{ padding: 50, textAlign: 'center' }}><h2>Loading homework…</h2></div>;
    if (error) return <div style={{ padding: 50, textAlign: 'center', color: 'red' }}><h2>Error</h2><p>{error}</p></div>;
    if (!data) return <div style={{ padding: 50, textAlign: 'center' }}><h2>Not found</h2></div>;

    const { parent, children } = data;
    const segmentationCost = parent?.costs?.scanGradeSegmentation?.totalCost || 0;
    const perQuestionSeg = parent?.scanGrade?.segmentationPerQuestion?.costs?.totalCost || 0;
    const totalGrading = children.reduce((acc, c) => acc + sumGradingCost(c.costs), 0);
    const totalCost = children.reduce((acc, c) => acc + (c.costs?.totalCost || 0), 0);

    return (
        <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <Link to="/cost-analysis?source=scangrade" style={{ fontSize: 13, color: '#3b82f6' }}>← Back to Scan &amp; Grade list</Link>
            <h1 style={{ marginTop: 16, fontSize: 22 }}>Scan &amp; Grade Homework</h1>
            <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#374151', marginBottom: 20 }}>{scanGradeHomeworkId}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                <Stat label="Questions" value={String(parent?.numQuestions ?? children.length)} />
                <Stat label="Total cost" value={formatCost(totalCost)} />
                <Stat label="Segmentation (raw)" value={formatCost(segmentationCost)} />
                <Stat label="Grading total" value={formatCost(totalGrading)} />
            </div>

            <h2 style={{ fontSize: 16, marginBottom: 8 }}>Per-question breakdown</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Each question carries its own grading cost plus an apportioned share of segmentation
                ({formatCost(perQuestionSeg)} per question).
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                        <th style={th}>#</th>
                        <th style={th}>scanGradeId</th>
                        <th style={th}>Segmentation</th>
                        <th style={th}>Grading</th>
                        <th style={th}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {children.map((c, idx) => {
                        const seg = c.costs?.scanGradeSegmentation?.totalCost || 0;
                        const grading = sumGradingCost(c.costs);
                        const total = c.costs?.totalCost || (seg + grading);
                        return (
                            <tr key={c.scanGradeId} style={{ borderTop: '1px solid #e5e7eb' }}>
                                <td style={td}>{idx + 1}</td>
                                <td style={{ ...td, fontFamily: 'monospace' }}>
                                    <Link to={`/scan-grade/question/${c.scanGradeId}`}>{c.scanGradeId}</Link>
                                </td>
                                <td style={td}>{formatCost(seg)}</td>
                                <td style={td}>{formatCost(grading)}</td>
                                <td style={{ ...td, fontWeight: 600 }}>{formatCost(total)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const th: React.CSSProperties = { padding: '8px 10px', fontWeight: 600, fontSize: 12, color: '#374151', borderBottom: '1px solid #e5e7eb' };
const td: React.CSSProperties = { padding: '10px' };

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fff' }}>
        <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
);

export default ScanGradeHomeworkPage;
